import { FC, useCallback, useState, HTMLAttributes } from 'react';

import ProProfileClientVerificationBadge from '@z-components/atoms/ProProfileClientVerificationBadge';
import TierExplanationModal from '@z-components/molecules/TierExplanationModal';
import LastOnlineStatus from '@z-components/molecules/ApplyListItem/LastOnlineStatus';
import TierItem from '@z-components/molecules/TierExplanationModal/TierItem';
import StarRating from '@z-components/atoms/StarRating';
import CloudinaryImage from '@z-components/utils/CloudinaryImage';
import { AvailablePrefecture } from '@z-components/molecules/types';

import LocationIcon from '@z-components/static/images/map-marker-blue.svg';
import WebsiteIcon from '@z-components/static/images/website-blue.svg';

import styles from './ProProfileClientContent.module.scss';
import PrefectureDetailModal from './PrefectureDetailModal';

const TRANSLATIONS = {
  en: {
    availableArea: 'Available Area:',
    call: 'Call',
    caseStudyCount: 'Case Study',
    counter: '',
    hiringAchievements: 'Hired',
    tier: {
      bronze: 'Bronze',
      diamond: 'Diamond',
      gold: 'Gold',
      'new-user': 'New User',
      platinum: 'Platinum',
      silver: 'Silver',
    },
  },
  ja: {
    availableArea: '対応可能エリア: ',
    call: '電話する',
    caseStudyCount: '事例掲載',
    counter: '件',
    hiringAchievements: '採用実績',
    tier: {
      bronze: 'ブロンズランク',
      diamond: 'ダイアモンドランク',
      gold: 'ゴールドランク',
      'new-user': 'ニューユーザー',
      platinum: 'プラチナランク',
      silver: 'シルバーランク',
    },
  },
};

interface Props {
  language: 'en' | 'ja';
  onReviewClick?: () => void;
  onWebsiteUrlClick?: HTMLAttributes<HTMLAnchorElement>['onClick'];
  proData: {
    availablePrefectures?: Array<AvailablePrefecture>;
    avatar?: string;
    lastOnline?: number;
    location?: string;
    myPageUrl?: string;
    phoneNumber?: string;
    proCaseStudiesCount?: number;
    proHiredCount?: number;
    tier: 'bronze' | 'diamond' | 'gold' | 'new-user' | 'platinum' | 'silver';
    title: string;
    verifiedBadges?: { governmentId: boolean; mail: boolean; phone: boolean };
    review?: {
      count: number;
      rating: number;
    };
    website?: string;
  };
}

const ProProfileClientContent: FC<Props> = ({
  language = 'ja',
  proData,
  onReviewClick,
  onWebsiteUrlClick,
}) => {
  const {
    availablePrefectures,
    avatar = '/static/images/empty-avatar.svg',
    location,
    tier,
    title,
    verifiedBadges,
    review,
    lastOnline,
    website,
    proCaseStudiesCount,
    proHiredCount,
  } = proData;

  const { governmentId, mail, phone } = verifiedBadges ?? {};

  const hasVerifiedBadge = governmentId ?? mail ?? phone;
  const tierText = TRANSLATIONS[language].tier[tier];

  const [showTierExplanationModal, setShowTierExplanationModal] = useState(false);
  const [showPrefectureDetailModal, setShowPrefectureDetailModal] = useState(false);
  const [selectedPrefecture, setSelectedPrefecture] = useState<AvailablePrefecture>();

  const onTierIconClick = useCallback(() => {
    setShowTierExplanationModal(true);
  }, []);

  const handleModalDismiss = useCallback(() => {
    setShowTierExplanationModal(false);
    setShowPrefectureDetailModal(false);
  }, []);

  const handleReviewClick = useCallback(() => {
    if (onReviewClick) onReviewClick();
  }, [onReviewClick]);

  const handleWebsiteUrlClick = useCallback(
    (event) => {
      if (onWebsiteUrlClick) {
        onWebsiteUrlClick(event);
      }
    },
    [onWebsiteUrlClick]
  );

  const handlePrefectureClick = useCallback(
    (index: number) => () => {
      if (!availablePrefectures || !availablePrefectures[0].cityGroups) return;

      setShowPrefectureDetailModal(true);
      setSelectedPrefecture(availablePrefectures[index]);
    },
    [availablePrefectures]
  );

  const proInfoSections = (
    <>
      {(website || location) && (
        <div className={styles['contact-section']}>
          {website && (
            <section className={styles['website-section']}>
              <WebsiteIcon height={16} width={16} />
              <a
                href={website}
                rel="noreferrer noopener"
                target="_blank"
                onClick={handleWebsiteUrlClick}
              >
                {website}
              </a>
            </section>
          )}

          {location && (
            <section className={styles['location-section']}>
              <LocationIcon height={16} width={16} />
              <p>{location}</p>
            </section>
          )}
        </div>
      )}

      {availablePrefectures && availablePrefectures.length > 0 && (
        <div className={styles['available-prefectures']}>
          <p className={styles['available-prefectures-text']}>
            {TRANSLATIONS[language].availableArea}
          </p>
          <section className={styles['available-prefectures-contents']}>
            {availablePrefectures.map((prefecture, index) => (
              <button key={prefecture.id} onClick={handlePrefectureClick(index)}>
                {prefecture.name}(
                <span className={styles['cities-count']}>{prefecture.citiesCount}</span>)
              </button>
            ))}
          </section>
        </div>
      )}
    </>
  );

  return (
    <>
      <article className={styles['pro-profile-content']}>
        <div className={styles['pro-top-container']}>
          <div className={styles['avatar-container']}>
            <CloudinaryImage
              className={styles['avatar']}
              url={avatar}
              width={110}
              isAsync={true}
              imageAlt={title}
            />
            <div className={styles['last-online-status-container']}>
              <LastOnlineStatus lastOnlineHour={lastOnline} />
            </div>
          </div>
          <div className={styles['details-container']}>
            <div className={styles['details']}>
              <section className={styles['title-section']}>
                <h1 className={styles['pro-title']}>{title}</h1>
                {review && review.count > 0 && (
                  <div className={styles['rating']}>
                    <StarRating height={14} rating={review.rating} />
                    <a className={styles['rating-count']} href="#" onClick={handleReviewClick}>
                      ({review.count})
                    </a>
                  </div>
                )}
              </section>
            </div>

            <div className={styles['review-badges-section']}>
              <button className={styles['tier-icon']} onClick={onTierIconClick}>
                <TierItem name={tier} text={tierText} smallSize={true} />
              </button>
              {hasVerifiedBadge && verifiedBadges && (
                <div className={styles['verification-badges-section']}>
                  <ProProfileClientVerificationBadge
                    verifiedBadges={verifiedBadges}
                    language={language}
                  />
                </div>
              )}
            </div>

            <div className={styles.desktop}>{proInfoSections}</div>
          </div>
          <div className={styles['achievements-container']}>
            <div className={styles['achievements-wrapper']}>
              {Boolean(proHiredCount && proHiredCount > 0) && (
                <div className={styles.achievements}>
                  <div className={styles.title}>{TRANSLATIONS[language].hiringAchievements}</div>
                  <div className={styles.count}>
                    <span className={styles['count-number']}>{proHiredCount}</span>
                    <span className={styles['counter-text']}>{TRANSLATIONS[language].counter}</span>
                  </div>
                </div>
              )}
              {Boolean(
                proHiredCount && proHiredCount > 0 && proCaseStudiesCount && proCaseStudiesCount > 0
              ) && <div className={styles.border} />}
              {Boolean(proCaseStudiesCount && proCaseStudiesCount > 0) && (
                <div className={styles.achievements}>
                  <div className={styles.title}>{TRANSLATIONS[language].caseStudyCount}</div>
                  <div className={styles.count}>
                    <span className={styles['count-number']}>{proCaseStudiesCount}</span>
                    <span className={styles['counter-text']}>{TRANSLATIONS[language].counter}</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className={styles.mobile}>{proInfoSections}</div>
      </article>

      {showTierExplanationModal && (
        <TierExplanationModal language={language} handleModalDismiss={handleModalDismiss} />
      )}

      {showPrefectureDetailModal && selectedPrefecture && (
        <PrefectureDetailModal
          prefecture={selectedPrefecture}
          language={language}
          handleModalDismiss={handleModalDismiss}
        />
      )}
    </>
  );
};

export default ProProfileClientContent;
export type { Props as ProProfileClientContentProps };
